import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import GoogleButton from "@/components/GoogleButton";
import RequiredMark from "@/components/RequiredMark";

function Req() {
    return <RequiredMark />;
}

// Safe redirect target — only allow same-origin relative paths so a malicious
// ?redirect=https://evil.com can't hijack the post-login step.
function safeRedirect(raw) {
    if (!raw) return null;
    if (typeof raw !== "string") return null;
    if (!raw.startsWith("/") || raw.startsWith("//")) return null;
    return raw;
}

export default function Login() {
    const { login, verify2fa } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const redirectTo = safeRedirect(params.get("redirect"));
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const [twofaTicket, setTwofaTicket] = useState("");
    const [twofaEmail, setTwofaEmail] = useState("");
    const [code, setCode] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const r = await login(email, password);
            if (r.twofa_required) {
                setTwofaTicket(r.ticket);
                setTwofaEmail(r.email || email);
                return;
            }
            nav(redirectTo || (r.role === "admin" ? "/admin" : "/dashboard"));
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const onVerify = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const u = await verify2fa(twofaTicket, code.trim());
            nav(redirectTo || (u.role === "admin" ? "/admin" : "/dashboard"));
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const cancel2fa = () => {
        setTwofaTicket("");
        setTwofaEmail("");
        setCode("");
        setErr("");
    };

    return (
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid grid-cols-12 gap-10" data-testid="login-page">
            <div className="col-span-12 md:col-span-5">
                <div className="overline mb-4">{twofaTicket ? "Two-step verification" : "Returning"}</div>
                <h1 className="font-display text-4xl md:text-5xl leading-tight">
                    {twofaTicket ? "Enter your code" : "Sign in"}
                </h1>
                <p className="text-[#c2c2c2] mt-6 leading-relaxed max-w-md">
                    {twofaTicket
                        ? `Open your authenticator app and enter the 6-digit code for ${twofaEmail}.`
                        : "Pick up where you left off, review past scans or download a report PDF."}
                </p>
            </div>
            <div className="col-span-12 md:col-span-7">
                {!twofaTicket ? (
                    <form
                        onSubmit={onSubmit}
                        className="border border-[#2a2a2a] bg-[#141414] p-8 max-w-xl"
                        data-testid="login-form"
                    >
                        <GoogleButton />
                        <div className="flex items-center gap-3 my-6">
                            <div className="flex-1 h-px bg-[#2a2a2a]" />
                            <span className="text-[13px] uppercase tracking-[0.12em] text-[#c2c2c2] font-semibold">
                                or with email
                            </span>
                            <div className="flex-1 h-px bg-[#2a2a2a]" />
                        </div>
                        <div className="mb-5">
                            <label className="label" htmlFor="login-email">Email<Req /></label>
                            <input
                                id="login-email"
                                type="email"
                                className="input-base"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                aria-required="true"
                                required
                                data-testid="login-email-input"
                            />
                        </div>
                        <div className="mb-6">
                            <label className="label" htmlFor="login-password">Password<Req /></label>
                            <input
                                id="login-password"
                                type="password"
                                className="input-base"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                aria-required="true"
                                required
                                data-testid="login-password-input"
                            />
                        </div>
                        {err && (
                            <div className="block-risk text-sm mb-4" data-testid="login-error">
                                {err}
                            </div>
                        )}
                        <button
                            type="submit"
                            className="btn-primary w-full md:w-auto"
                            disabled={loading}
                            data-testid="login-submit-button"
                        >
                            {loading ? "Signing in…" : "Sign in"}
                        </button>
                        <div className="mt-6 text-sm text-[#c2c2c2]">
                            New here?{" "}
                            <Link
                                to={redirectTo ? `/register?redirect=${encodeURIComponent(redirectTo)}` : "/register"}
                                className="text-[#00A896] hover:underline"
                                data-testid="login-to-register-link"
                            >
                                Create an account
                            </Link>
                        </div>
                    </form>
                ) : (
                    <form
                        onSubmit={onVerify}
                        className="border border-[#2ec5b6]/40 bg-[#0f1a19] p-8 max-w-xl"
                        data-testid="login-2fa-form"
                    >
                        <div className="mb-6">
                            <label className="label" htmlFor="twofa-code">Six-digit code<Req /></label>
                            <input
                                id="twofa-code"
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                pattern="[0-9]{6}"
                                maxLength={6}
                                className="input-base font-mono tracking-[0.4em] text-2xl text-center"
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                placeholder="123456"
                                aria-required="true"
                                required
                                autoFocus
                                data-testid="login-2fa-code-input"
                            />
                        </div>
                        {err && (
                            <div className="block-risk text-sm mb-4" data-testid="login-2fa-error">
                                {err}
                            </div>
                        )}
                        <div className="flex items-center gap-3">
                            <button
                                type="submit"
                                className="btn-primary"
                                disabled={loading || code.length !== 6}
                                data-testid="login-2fa-submit-button"
                            >
                                {loading ? "Verifying…" : "Verify & sign in"}
                            </button>
                            <button
                                type="button"
                                className="btn-ghost"
                                onClick={cancel2fa}
                                disabled={loading}
                                data-testid="login-2fa-cancel-button"
                            >
                                Use different account
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
